/**
 * Product Data Hooks
 * Specialized hooks for fetching product/model-related data.
 * Built on top of useApi for caching and deduplication.
 */

import { useMemo, useState, useEffect } from 'react';
import useApi from './useApi';
import { productService } from '../services';
import { formatPriceWithUnit } from '../utils/formatPrice';
import { formatDate } from '../utils/formatDate';
import { CLASSIFICATION_COLORS } from '../constants/models';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const formatPrice = (value, currency, unit) => {
  if (value === null || value === undefined) return '';
  return formatPriceWithUnit(value, currency, unit);
};

/**
 * Factory for the common pattern:
 *   useApi(fetcher(productId), { enabled, cacheKey, cacheTTL, dependencies, context })
 *
 * Used for all simple "fetch by productId, return raw data" hooks.
 *
 * @param {(id: number) => Promise} fetcher
 * @param {(id: number) => string} cacheKeyFn
 * @param {number} ttl - Cache TTL in ms
 * @param {string} endpoint - Context label for debugging
 */
const makeProductIdHook = (fetcher, cacheKeyFn, ttl, endpoint) =>
  (productId, options = {}) =>
    useApi(
      () => fetcher(productId),
      {
        ...options,
        enabled: !!productId,
        cacheKey: cacheKeyFn(productId),
        cache: options.cache !== false,
        cacheTTL: options.cacheTTL || ttl,
        dependencies: [productId],
        context: { module: 'Products', endpoint },
      }
    );

// ---------------------------------------------------------------------------
// Simple productId hooks (generated via factory)
// ---------------------------------------------------------------------------

/** Fetch product risk assessment */
export const useProductRisk = makeProductIdHook(
  id => productService.getRisk(id),
  id => `product-risk-${id}`,
  15 * 60 * 1000,
  'risk'
);

/** Fetch product regulatory entries */
export const useProductRegulatory = makeProductIdHook(
  id => productService.getRegulatory(id),
  id => `product-regulatory-${id}`,
  15 * 60 * 1000,
  'regulatory'
);

/** Fetch product insights */
export const useProductInsights = makeProductIdHook(
  id => productService.getInsights(id),
  id => `product-insights-${id}`,
  10 * 60 * 1000,
  'insights'
);

/** Fetch product sources (country breakdown) */
export const useProductSources = makeProductIdHook(
  id => productService.getSources(id),
  id => `product-sources-${id}`,
  30 * 60 * 1000,
  'sources'
);

/** Fetch product rolling averages (7d/7w/7y) */
export const useProductRollingAverages = makeProductIdHook(
  id => productService.getRollingAverages(id),
  id => `product-rolling-averages-${id}`,
  5 * 60 * 1000,
  'rollingAverages'
);

/** Fetch product rolling average series (bucketed by day/week/month/year) */
export const useProductRollingAverageSeries = makeProductIdHook(
  id => productService.getRollingAverageSeries(id),
  id => `product-rolling-average-series-${id}`,
  5 * 60 * 1000,
  'rollingAverageSeries'
);

/** Fetch product chart overview (monthly series) */
export const useProductChartOverview = makeProductIdHook(
  id => productService.getChartOverview(id),
  id => `product-chart-overview-${id}`,
  10 * 60 * 1000,
  'chartOverview'
);

// ---------------------------------------------------------------------------
// Hooks with extra params or data transformations
// ---------------------------------------------------------------------------

/**
 * Fetch paginated product list.
 * Returns: { items[], total, limit, offset, has_more }
 */
export const useProductList = (options = {}) => {
  const { limit = 50, offset = 0, ...restOptions } = options;

  return useApi(
    () => productService.list({ limit, offset }),
    {
      ...restOptions,
      cacheKey: `product-list-${limit}-${offset}`,
      cache: restOptions.cache !== false,
      cacheTTL: restOptions.cacheTTL || 2 * 60 * 1000,
      dependencies: [limit, offset],
      context: { module: 'Products', endpoint: 'list' },
    }
  );
};

/**
 * Fetch product overview (dashboard data).
 * Returns recent views, your models, demo models, and sectors in one call.
 * Triggers secondary per-product chart data fetches after the overview loads.
 *
 * @param {Object} options
 * @param {number} options.limitRecent - Max recent views (default 8)
 * @param {number} options.limitModels - Max user models (default 30)
 */
export const useProductOverview = (options = {}) => {
  const { limitRecent = 8, limitModels = 30, ...restOptions } = options;

  const result = useApi(
    () => productService.getOverview({ limit_recent: limitRecent, limit_models: limitModels }),
    {
      ...restOptions,
      cacheKey: `product-overview-${limitRecent}-${limitModels}`,
      cache: restOptions.cache !== false,
      cacheTTL: restOptions.cacheTTL || 2 * 60 * 1000,
      dependencies: [limitRecent, limitModels],
      context: { module: 'Products', endpoint: 'overview' },
    }
  );

  // Fetch mini chart data for each product visible in the overview
  const [chartDataMap, setChartDataMap] = useState({});

  useEffect(() => {
    if (!result.data) return;

    const allProducts = [
      ...(result.data.recent_views || []),
      ...(result.data.your_models || []),
      ...(result.data.demo_models || []),
    ];
    const uniqueIds = [...new Set(allProducts.map(p => p.id))];
    if (uniqueIds.length === 0) return;

    let cancelled = false;

    const fetchChartData = async () => {
      const entries = await Promise.allSettled(
        uniqueIds.map(async (id) => {
          const data = await productService.getChartOverview(id);
          return { id, data };
        })
      );

      if (cancelled) return;

      const map = {};
      entries.forEach(entry => {
        if (entry.status === 'fulfilled') {
          const { id, data } = entry.value;
          map[id] = Array.isArray(data) ? data.map(point => point.value) : [];
        }
      });
      setChartDataMap(map);
    };

    fetchChartData();
    return () => { cancelled = true; };
  }, [result.data]);

  const transformedData = useMemo(() => {
    if (!result.data) return null;

    const transformProduct = (product) => ({
      id: product.id,
      name: product.name,
      description: product.description,
      image_url: product.image_url,
      is_demo: product.is_demo,
      owner_id: product.owner_id,
      last_price_value: product.last_price_value,
      last_price_currency: product.last_price_currency,
      last_price_unit: product.last_price_unit,
      last_price_date: product.last_price_date,
      updated_at: product.updated_at,
      price: formatPrice(product.last_price_value, product.last_price_currency, product.last_price_unit),
      date: formatDate(product.last_price_date),
      chartData: chartDataMap[product.id] || [],
    });

    return {
      has_models: result.data.has_models,
      recent_views: result.data.recent_views?.map(transformProduct) || [],
      your_models: result.data.your_models?.map(transformProduct) || [],
      demo_models: result.data.demo_models?.map(transformProduct) || [],
      related_fields: result.data.related_fields || [],
      all_sectors: result.data.all_sectors || [],
    };
  }, [result.data, chartDataMap]);

  return { ...result, data: transformedData };
};

/** Fetch product sectors */
export const useProductSectors = (options = {}) =>
  useApi(
    () => productService.getSectors(),
    {
      ...options,
      cacheKey: 'product-sectors',
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 30 * 60 * 1000,
      context: { module: 'Products', endpoint: 'sectors' },
    }
  );

/**
 * Fetch recently viewed products for the authenticated user.
 * @param {Object} options
 * @param {number} options.limit - Max recent views (default 8)
 */
export const useProductRecentViews = (options = {}) => {
  const { limit = 8, ...restOptions } = options;

  const result = useApi(
    () => productService.getRecentViews({ limit }),
    {
      ...restOptions,
      cacheKey: `product-recent-views-${limit}`,
      cache: restOptions.cache !== false,
      cacheTTL: restOptions.cacheTTL || 2 * 60 * 1000,
      dependencies: [limit],
      context: { module: 'Products', endpoint: 'recentViews', limit },
    }
  );

  const transformedData = useMemo(() => {
    if (!result.data) return null;
    return result.data.map(product => ({
      id: product.id,
      name: product.name,
      description: product.description,
      image_url: product.image_url,
      is_demo: product.is_demo,
      owner_id: product.owner_id,
      last_price_value: product.last_price_value,
      last_price_currency: product.last_price_currency,
      last_price_unit: product.last_price_unit,
      last_price_date: product.last_price_date,
      updated_at: product.updated_at,
      price: formatPrice(product.last_price_value, product.last_price_currency, product.last_price_unit),
      date: formatDate(product.last_price_date),
      chartData: [],
    }));
  }, [result.data]);

  return { ...result, data: transformedData };
};

/**
 * Fetch related sectors for the authenticated user.
 * @param {Object} options
 * @param {number} options.limit - Max sectors (default 12)
 */
export const useProductRelatedSectors = (options = {}) => {
  const { limit = 12, ...restOptions } = options;

  return useApi(
    () => productService.getRelatedSectors({ limit }),
    {
      ...restOptions,
      cacheKey: `product-related-sectors-${limit}`,
      cache: restOptions.cache !== false,
      cacheTTL: restOptions.cacheTTL || 10 * 60 * 1000,
      dependencies: [limit],
      context: { module: 'Products', endpoint: 'relatedSectors', limit },
    }
  );
};

/**
 * Fetch products in a sector (with list groupings).
 * @param {number} sectorId
 */
export const useSectorProducts = (sectorId, options = {}) =>
  useApi(
    () => productService.getSectorLists(sectorId),
    {
      ...options,
      enabled: !!sectorId,
      cacheKey: `sector-products-${sectorId}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 10 * 60 * 1000,
      dependencies: [sectorId],
      context: { module: 'Products', endpoint: 'sectorLists', sectorId },
    }
  );

/**
 * Fetch full product detail including components.
 *
 * Returns transformed data:
 * { id, owner_id, name, description, image_url, is_demo, default_currency,
 *   base_unit, base_quantity, last_price_value, last_price_currency,
 *   last_price_unit, last_price_date, created_at, updated_at,
 *   sectors, components, price, date, createdDate, updatedDate }
 */
export const useProductDetail = (productId, options = {}) => {
  const result = useApi(
    () => productService.getDetail(productId),
    {
      ...options,
      enabled: !!productId,
      cacheKey: `product-detail-${productId}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 5 * 60 * 1000,
      dependencies: [productId],
      context: { module: 'Products', endpoint: 'detail' },
    }
  );

  const transformedData = useMemo(() => {
    if (!result.data) return null;
    const product = result.data;
    return {
      ...product,
      price: formatPrice(product.last_price_value, product.last_price_currency, product.last_price_unit),
      date: formatDate(product.last_price_date),
      createdDate: formatDate(product.created_at),
      updatedDate: formatDate(product.updated_at),
      components: product.components?.map(comp => ({ ...comp })) || [],
    };
  }, [result.data]);

  return { ...result, data: transformedData };
};

/**
 * Fetch product general info (price display, change, vat, etc.)
 *
 * Returns transformed data:
 * { name, description, vat_percent, last_price_display, last_price_value,
 *   currency, value_unit, last_price_date_display, last_price_date_iso,
 *   change_display, change_abs_value, change_pct_value, price, date }
 */
export const useProductGeneral = (productId, options = {}) => {
  const result = useApi(
    () => productService.getGeneral(productId),
    {
      ...options,
      enabled: !!productId,
      cacheKey: `product-general-${productId}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 5 * 60 * 1000,
      dependencies: [productId],
      context: { module: 'Products', endpoint: 'general' },
    }
  );

  const transformedData = useMemo(() => {
    if (!result.data) return null;
    const data = result.data;
    return {
      ...data,
      price: data.last_price_display || formatPrice(data.last_price_value, data.currency, data.value_unit),
      date: data.last_price_date_display || formatDate(data.last_price_date_iso),
    };
  }, [result.data]);

  return { ...result, data: transformedData };
};

/**
 * Fetch product chart comparison series (OHLC for multiple products).
 *
 * @param {number} productId - Base product ID
 * @param {Object} params - { ids: string, start: string, end: string, tf?: string }
 */
export const useProductChartComparison = (productId, params = {}, options = {}) => {
  const paramsString = JSON.stringify(params);

  return useApi(
    () => productService.getChartComparison(productId, params),
    {
      ...options,
      enabled: !!productId && !!params.ids && !!params.start && !!params.end,
      cacheKey: `product-chart-comparison-${productId}-${paramsString}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 2 * 60 * 1000,
      dependencies: [productId, paramsString],
      context: { module: 'Products', endpoint: 'chartComparison', params },
    }
  );
};

/**
 * Fetch product chart series (OHLC).
 *
 * @param {number} productId
 * @param {Object} params - { start: string, end: string, tf?: string }
 */
export const useProductChartSeries = (productId, params = {}, options = {}) => {
  const paramsString = JSON.stringify(params);

  return useApi(
    () => productService.getChartSeries(productId, params),
    {
      ...options,
      enabled: !!productId && !!params.start && !!params.end,
      cacheKey: `product-chart-series-${productId}-${paramsString}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 2 * 60 * 1000,
      dependencies: [productId, paramsString],
      context: { module: 'Products', endpoint: 'chartSeries', params },
    }
  );
};

/**
 * Fetch product classification (sector breakdown).
 * Transforms raw API array into ClassificationPieChart format:
 * { materials: [{ name, value, color, sectorId, rawValue }] }
 */
export const useProductClassification = (productId, options = {}) => {
  const result = useApi(
    () => productService.getClassification(productId),
    {
      ...options,
      enabled: !!productId,
      cacheKey: `product-classification-${productId}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 15 * 60 * 1000,
      dependencies: [productId],
      context: { module: 'Products', endpoint: 'classification' },
    }
  );

  const transformedData = useMemo(() => {
    if (!result.data) return null;
    const sectors = Array.isArray(result.data) ? result.data : [];
    return {
      materials: sectors.map((sector, index) => ({
        name: sector.sector_name,
        value: Math.round(sector.percent),
        color: CLASSIFICATION_COLORS[index % CLASSIFICATION_COLORS.length],
        sectorId: sector.sector_id,
        rawValue: sector.value,
      })),
    };
  }, [result.data]);

  return { ...result, data: transformedData };
};

/**
 * Fetch recent daily aggregated price points.
 * @param {number} productId
 * @param {Object} options
 * @param {number} options.limit - Max points (default 5)
 */
export const useProductRecentDaily = (productId, options = {}) => {
  const { limit = 5, ...restOptions } = options;

  return useApi(
    () => productService.getRecentDaily(productId, { limit }),
    {
      ...restOptions,
      enabled: !!productId,
      cacheKey: `product-recent-daily-${productId}-${limit}`,
      cache: restOptions.cache !== false,
      cacheTTL: restOptions.cacheTTL || 2 * 60 * 1000,
      dependencies: [productId, limit],
      context: { module: 'Products', endpoint: 'recentDaily' },
    }
  );
};

/**
 * Fetch product table average data.
 * @param {number} productId
 * @param {Object} params - { limit?, weeks?, months?, years? }
 */
export const useProductTableAverage = (productId, params = {}, options = {}) => {
  const paramsString = JSON.stringify(params);

  return useApi(
    () => productService.getTableAverage(productId, params),
    {
      ...options,
      enabled: !!productId,
      cacheKey: `product-table-average-${productId}-${paramsString}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 5 * 60 * 1000,
      dependencies: [productId, paramsString],
      context: { module: 'Products', endpoint: 'tableAverage', params },
    }
  );
};

/**
 * Search products.
 * @param {string} query
 * @param {'text'|'ai'} searchType
 */
export const useProductSearch = (query, searchType = 'text', options = {}) => {
  const { limit, ...restOptions } = options;

  const result = useApi(
    () => searchType === 'ai'
      ? productService.promptSearch({ query }, { limit: limit || 50 })
      : productService.search({ query, ...(limit ? { limit } : {}) }),
    {
      ...restOptions,
      enabled: !!query && query.length > 0,
      cacheKey: `product-search-${searchType}-${query}`,
      cache: restOptions.cache !== false,
      cacheTTL: restOptions.cacheTTL || 1 * 60 * 1000,
      dependencies: [query, searchType],
      context: { module: 'Products', endpoint: 'search', query, searchType },
    }
  );

  const transformedData = useMemo(() => {
    if (!result.data) return null;
    const rawResults = result.data.results || [];
    return {
      query: result.data.query,
      results: rawResults.map(item => ({
        ...(item.product || item),
        sectors: item.sectors || [],
        is_owner: item.is_owner,
      })),
    };
  }, [result.data]);

  return { ...result, data: transformedData };
};

/** Fetch all products (used for comparison dropdowns). */
export const useAllProducts = (options = {}) =>
  useApi(
    () => productService.list(),
    {
      ...options,
      cacheKey: 'products-list-all',
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 5 * 60 * 1000,
      context: { module: 'Products', endpoint: 'list' },
    }
  );

/** Search products for selection dropdowns. */
export const useProductSearchOptions = (query, options = {}) =>
  useApi(
    () => productService.searchOptions({ query }),
    {
      ...options,
      enabled: options.enabled !== false && !!query && query.length > 0,
      cacheKey: `product-search-options-${query}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 1 * 60 * 1000,
      dependencies: [query],
      context: { module: 'Products', endpoint: 'searchOptions', query },
    }
  );

/**
 * Composite hook that fetches all secondary product page data in parallel.
 * Returns named results plus aggregate loading/error states and a refetchAll helper.
 */
export const useProductAllData = (productId, options = {}) => {
  const general = useProductGeneral(productId, options);
  const chartOverview = useProductChartOverview(productId, options);
  const classification = useProductClassification(productId, options);
  const risk = useProductRisk(productId, options);
  const regulatory = useProductRegulatory(productId, options);
  const insights = useProductInsights(productId, options);
  const tableAverage = useProductTableAverage(productId, options);
  const sources = useProductSources(productId, options);

  const loading = useMemo(
    () => general.loading || chartOverview.loading || classification.loading ||
          risk.loading || regulatory.loading || insights.loading ||
          tableAverage.loading || sources.loading,
    [general.loading, chartOverview.loading, classification.loading,
     risk.loading, regulatory.loading, insights.loading,
     tableAverage.loading, sources.loading]
  );

  const error = useMemo(
    () => general.error || chartOverview.error || classification.error ||
          risk.error || regulatory.error || insights.error ||
          tableAverage.error || sources.error,
    [general.error, chartOverview.error, classification.error,
     risk.error, regulatory.error, insights.error,
     tableAverage.error, sources.error]
  );

  const refetchAll = () => {
    general.refetch();
    chartOverview.refetch();
    classification.refetch();
    risk.refetch();
    regulatory.refetch();
    insights.refetch();
    tableAverage.refetch();
    sources.refetch();
  };

  return { general, chartOverview, classification, risk, regulatory, insights, tableAverage, sources, loading, error, refetchAll };
};

export default useProductOverview;
